import { useState } from "react";
import {
  ArrowUTurnLeft,
  CrossIcon,
  DocumentTextIcon,
  SpinnerIcon,
} from "../../Icons";
import EditableField from "./EditableField";
import { CompleteArray } from "../../../types/FlowTemplates";
import EditTemplateTitle from "./EditTemplateTitle";
import { toast } from "react-toastify";
import { useAddFlowTemplateMutation } from "../../../store/apiSlices/flowTemplates";
import FlowBuilder from "./FlowBuilder";

const INITIAL_TEMPLATE: CompleteArray[] = [
  {
    step: {
      field: "Shipment Booking",
      complted: false,
    },
    Contacts: [],
    tasks: [],
    document_vault: [],
  },
];

export default function CustomTemplateCard({
  toggleCustomTemplate,
}: {
  toggleCustomTemplate: () => void;
}) {
  const [steps, setSteps] = useState<CompleteArray[]>(INITIAL_TEMPLATE);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const [titleValue, setTitleValue] = useState("Enter Template Name");

  const [addFlowTemplate, { isLoading: addLoading }] =
    useAddFlowTemplateMutation();

  const handleOnDropStep = (idx: number) => {
    if (dragIndex === null || dragIndex === idx) return;
    const updated = [...steps];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(idx, 0, moved);
    setSteps(updated);
    setDragIndex(null);
  };

  const insertStep = (idx: number) => {
    const newStep: CompleteArray = {
      step: { field: "New Step", complted: false },
      Contacts: [],
      tasks: [],
      document_vault: [],
    };
    const updated = [...steps];
    updated.splice(idx + 1, 0, newStep);
    setSteps(updated);
  };

  const handleRemoveStep = (idx: number) => {
    const updated = [...steps];
    updated.splice(idx, 1);
    setSteps(updated);
  };

  const handleSave = async () => {
    if (!titleValue) {
      toast.warning("Title can not be title");

      return;
    }

    if (titleValue === "Enter Template Name") {
      toast.warning("Enter a template name, double click to edit");

      return;
    }

    const payload = {
      title: titleValue,
      type: "custom",
      complete_array: steps,
    };
    // console.log(payload);
    const { data } = await addFlowTemplate(payload);
    if (data.status === "success" && data.id) {
      toast.success(data.message);
      toggleCustomTemplate();
    } else {
      toast.error("Something went wrong! try again.");
    }
  };

  return (
    <div className="bg-white text-gray-800 rounded-xl p-6 border border-[#f3f3f5] space-y-6">
      <div className="flex items-center justify-between">
        <EditTemplateTitle title={titleValue} setTitleValue={setTitleValue} />

        <div className="flex items-center gap-5">
          <button
            onClick={handleSave}
            className="px-2 py-1 flex items-center gap-1 rounded-lg hover:bg-gray-100 disabled:cursor-not-allowed"
            disabled={addLoading}
          >
            {addLoading ? (
              <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
            ) : (
              <DocumentTextIcon className="size-4" />
            )}
            Save
          </button>

          <button
            onClick={() => {
              setSteps(INITIAL_TEMPLATE);
            }}
            className="px-2 py-1 flex items-center gap-1 rounded-lg hover:bg-gray-100 disabled:cursor-not-allowed"
            disabled={addLoading}
          >
            <ArrowUTurnLeft className="size-4" />
            Reset
          </button>

          <button
            onClick={toggleCustomTemplate}
            className="hover:scale-90 hover:text-red-400"
          >
            <CrossIcon className="size-6" />
          </button>
        </div>
      </div>

      <FlowBuilder>
        <FlowBuilder.Track lineStart={0}>
          {steps.map((el, idx) => (
            <FlowBuilder.Item key={idx}>
              {idx === 0 && (
                <FlowBuilder.AddBtn onClick={() => insertStep(idx - 1)} />
              )}

              <FlowBuilder.Step
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleOnDropStep(idx)}
              >
                <FlowBuilder.Badge>{idx + 1}</FlowBuilder.Badge>

                <EditableField {...{ el, idx, steps, setSteps }} />

                {!["Shipment Pickup", "Shipment Booking"].includes(
                  el.step.field,
                ) && (
                  <FlowBuilder.RemoveBtn
                    onClick={() => handleRemoveStep(idx)}
                  />
                )}
              </FlowBuilder.Step>

              <FlowBuilder.AddBtn onClick={() => insertStep(idx)} />
            </FlowBuilder.Item>
          ))}
        </FlowBuilder.Track>
      </FlowBuilder>
    </div>
  );
}
